import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Search } from "lucide-react";
import api from "@/lib/api";

export default function SaleForm() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers.slice(0, 10);
    return customers.filter((c) => c.name.toLowerCase().includes(customerSearch.toLowerCase()) || c.email.toLowerCase().includes(customerSearch.toLowerCase())).slice(0, 10);
  }, [customers, customerSearch]);

  const addProduct = (product) => {
    const existing = items.find((i) => i.product_id === product.id);
    if (existing) setItems(items.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    else setItems([...items, { product_id: product.id, product_name: product.name, quantity: 1, unit_price: product.price, monthly_price: product.monthly_price || 0 }]);
  };

  const removeItem = (productId) => setItems(items.filter((i) => i.product_id !== productId));
  const updateQuantity = (productId, qty) => { if (qty < 1) return; setItems(items.map((i) => i.product_id === productId ? { ...i, quantity: qty } : i)); };

  const total = items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0);
  const monthlyTotal = items.reduce((sum, i) => sum + (i.monthly_price || 0) * i.quantity, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) { setError("Selecione um cliente"); return; }
    if (items.length === 0) { setError("Adicione pelo menos um produto"); return; }
    setError(""); setSaving(true);
    try {
      await api.post("/sales", { customer_id: selectedCustomer.id, customer_name: selectedCustomer.name, items, total, monthly_total: monthlyTotal, notes });
      navigate("/crm/sales");
    } catch { setError("Erro ao salvar venda."); }
    setSaving(false);
  };

  return (
    <div data-testid="sale-form-page">
      <button onClick={() => navigate("/crm/sales")} className="inline-flex items-center gap-2 text-sm mb-6 transition-colors" style={{ color: "var(--crm-text-muted)" }}>
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="crm-heading mb-8">Nova Venda</h1>

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-6">
        {error && <div className="p-4 rounded-xl text-sm" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444" }}>{error}</div>}

        {/* Customer */}
        <div className="crm-card p-6">
          <h2 className="crm-subheading">Cliente</h2>
          {selectedCustomer ? (
            <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: "var(--crm-input-bg)", border: "1px solid var(--crm-border)" }}>
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--crm-text)" }}>{selectedCustomer.name}</p>
                <p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>{selectedCustomer.email}</p>
              </div>
              <button type="button" onClick={() => { setSelectedCustomer(null); setCustomerSearch(""); }} className="text-xs" style={{ color: "var(--crm-accent)" }}>Alterar</button>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
              <input value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }} onFocus={() => setShowCustomerDropdown(true)}
                data-testid="sale-customer-search" placeholder="Buscar cliente..." className="crm-input pl-10" />
              {showCustomerDropdown && filteredCustomers.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden z-10 max-h-48 overflow-y-auto" style={{ background: "var(--crm-surface)", border: "1px solid var(--crm-border)", boxShadow: "var(--crm-shadow-lg)" }}>
                  {filteredCustomers.map((c) => (
                    <button key={c.id} type="button" onClick={() => { setSelectedCustomer(c); setShowCustomerDropdown(false); }}
                      data-testid={`customer-option-${c.id}`} className="w-full text-left px-4 py-3 transition-colors" style={{ borderBottom: "1px solid var(--crm-border)" }}>
                      <p className="text-sm" style={{ color: "var(--crm-text)" }}>{c.name}</p>
                      <p className="text-xs" style={{ color: "var(--crm-text-dim)" }}>{c.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Products */}
        <div className="crm-card p-6">
          <h2 className="crm-subheading">Produtos</h2>
          <div className="flex flex-wrap gap-2 mb-5">
            {products.map((p) => (
              <button key={p.id} type="button" onClick={() => addProduct(p)} data-testid={`add-product-${p.id}`} className="crm-btn-secondary text-xs py-2 px-3">
                <Plus className="w-3 h-3" /> {p.name}
              </button>
            ))}
            {products.length === 0 && <p className="text-sm" style={{ color: "var(--crm-text-dim)" }}>Nenhum produto cadastrado.</p>}
          </div>

          {items.length > 0 && (
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--crm-border)" }}>
              <table className="crm-table">
                <thead><tr><th>Produto</th><th className="text-center">Qtd</th><th className="text-right">Preço</th><th className="text-right">Mensal</th><th className="w-10"></th></tr></thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.product_id}>
                      <td className="primary">{item.product_name}</td>
                      <td className="text-center">
                        <input type="number" min="1" value={item.quantity} onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value) || 1)}
                          className="crm-input w-16 text-center py-1.5 px-2" />
                      </td>
                      <td className="text-right" style={{ fontWeight: 500 }}>¥{(item.unit_price * item.quantity).toLocaleString()}</td>
                      <td className="text-right">{item.monthly_price ? `¥${(item.monthly_price * item.quantity).toLocaleString()}` : "—"}</td>
                      <td className="text-right">
                        <button type="button" onClick={() => removeItem(item.product_id)} className="p-1.5 hover:text-red-400" style={{ color: "var(--crm-text-dim)" }}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {items.length > 0 && (
            <div className="mt-5 flex justify-end">
              <div className="text-right space-y-1">
                <p className="text-sm" style={{ color: "var(--crm-text-muted)" }}>Total: <span className="text-xl font-bold" style={{ color: "var(--crm-text)" }}>¥{total.toLocaleString()}</span></p>
                {monthlyTotal > 0 && <p className="text-sm" style={{ color: "var(--crm-text-muted)" }}>Mensal: <span className="font-semibold" style={{ color: "var(--crm-accent)" }}>¥{monthlyTotal.toLocaleString()}/mês</span></p>}
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="crm-card p-6">
          <h2 className="crm-subheading">Observações</h2>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sale-notes" rows={3} className="crm-input resize-none" placeholder="Observações (opcional)" />
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="sale-save-btn" className="crm-btn-primary">{saving ? "Salvando..." : "Finalizar Venda"}</button>
          <button type="button" onClick={() => navigate("/crm/sales")} className="crm-btn-secondary">Cancelar</button>
        </div>
      </form>
    </div>
  );
}
